import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";
import SquareIcon from "@mui/icons-material/Square";
import moment from "moment";
import { HexColorPicker } from "react-colorful";
import { API, graphqlOperation } from "aws-amplify";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";

import useErrors from "hooks/useErrors";
import {
  configInitialValues,
  periods,
  scheduleTypes,
} from "../../config/constants";
import DateRangeForm from "components/utils/DateRangeForm";
import { globalStrings } from "config/translations/es";
import { deviceVariablesByDeviceID, listVariables } from "graphql/queries";
import toast from "react-hot-toast";
import LabelRequired from "components/utils/LabelRequired";
import CustomPopover from "components/utils/CustomPopover";

const DashboardCardForm = ({
  onSubmit,
  initialValues = configInitialValues,
}) => {
  const [modalDateVisible, setModalDateVisible] = useState(false);
  const [deviceVariables, setDeviceVariables] = useState([]);
  const [dashboardCardFormState, setDashboardCardFormState] =
    useState(initialValues);
  const [errors, setErrors] = useState({});
  const validateErrors = useErrors({
    variableId: "isRequired",
    period: "isRequired",
    scheduleType: "isRequired",
    chartColor: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(dashboardCardFormState).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (dashboardCardFormState.period !== periods[periods.length - 1]) {
      dashboardCardFormState.dateFrom = "";
      dashboardCardFormState.dateTo = "";
      dashboardCardFormState.groupBy = "";
    } else {
      if (
        !checkGroupBy(
          dashboardCardFormState.dateFrom,
          dashboardCardFormState.dateTo,
          dashboardCardFormState.groupBy
        )
      ) {
        toast.error(strings.responses.invalidGroup);
        return;
      }
    }

    if (!hasErrors)
      onSubmit({
        ...dashboardCardFormState,
      });
  };

  const checkGroupBy = (from, to, group) => {
    return (
      moment(to).diff(moment(from), group, true) >= 1 &&
      moment(to).diff(moment(from), group, true) <= 60
    );
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "period") {
      setDashboardCardFormState((prevSt) => ({
        ...prevSt,
        dateFrom: moment().startOf("day").toDate(),
        dateTo: moment().endOf("day").toDate(),
      }));
    }
    setDashboardCardFormState((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeColor = (color) => {
    setDashboardCardFormState((prevSt) => ({
      ...prevSt,
      chartColor: color,
    }));
  };

  const onOpenModalDate = () => setModalDateVisible(true);

  const onSubmitDateRange = (date) => {
    const { from, to } = date;

    setDashboardCardFormState((prevSt) => ({
      ...prevSt,
      period: periods[periods.length - 1],
      dateFrom: moment(from).toDate(),
      dateTo: moment(to).toDate(),
    }));
    setModalDateVisible(false);
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({});
    setDashboardCardFormState(initialValues);
  };

  async function fetchDeviceVariables(deviceID) {
    try {
      const deviceVariablesData = await API.graphql(
        graphqlOperation(deviceVariablesByDeviceID, {
          deviceID,
        })
      );
      const deviceVariablesItems =
        deviceVariablesData.data.deviceVariablesByDeviceID.items;

      if (deviceVariablesItems?.length) {
        const variablesData = await API.graphql(
          graphqlOperation(listVariables, {
            filter: {
              or: deviceVariablesItems?.map((devVar) => ({
                id: { eq: devVar.variableID },
              })),
            },
          })
        );
        const variablesItems = variablesData.data.listVariables.items;

        setDeviceVariables(variablesItems);
      } else setDeviceVariables([]);
    } catch (err) {
      toast.error(strings.responses.errorFetchVariables);
    }
  }

  async function fetchVariables() {
    try {
      const variablesData = await API.graphql(graphqlOperation(listVariables));
      const variablesItems = variablesData.data.listVariables.items;

      setDeviceVariables(variablesItems);
    } catch (err) {
      toast.error(strings.responses.errorFetchVariables);
    }
  }

  useEffect(() => {
    setDashboardCardFormState(initialValues);
    if (!!initialValues?.deviceId)
      fetchDeviceVariables(initialValues?.deviceId);
    if (
      initialValues?.graphBy === "company" ||
      initialValues?.graphBy === "branch"
    )
      fetchVariables();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.deviceId, initialValues?.graphBy]);

  return (
    <>
      <Grid
        rowGap="15px"
        columnGap="15px"
        padding="20px"
        templateColumns={{ base: "1fr", large: "2fr 1fr 1fr" }}
      >
        <SelectField
          name="variableId"
          value={dashboardCardFormState.variableId}
          label={<LabelRequired text={strings.fields.variable.label} />}
          onChange={onChange}
          errorMessage={errors["variableId"]}
          hasError={!!errors["variableId"]}
        >
          <option value="">{strings.selectOption}</option>
          {deviceVariables?.map((devVar) => (
            <option value={devVar.id}>{devVar.name}</option>
          ))}
        </SelectField>
        <SelectField
          name="scheduleType"
          value={dashboardCardFormState.scheduleType}
          label={<LabelRequired text={strings.fields.scheduleType.label} />}
          onChange={onChange}
          errorMessage={errors["scheduleType"]}
          hasError={!!errors["scheduleType"]}
        >
          {scheduleTypes.map((val) => (
            <option value={val}>{globalStrings.scheduleTypes[val]}</option>
          ))}
        </SelectField>
        <TextField
          name="chartColor"
          label={<LabelRequired text={strings.fields.chartColor.label} />}
          value={dashboardCardFormState.chartColor}
          onChange={onChange}
          errorMessage={errors["chartColor"]}
          hasError={!!errors["chartColor"]}
          outerEndComponent={
            <CustomPopover
              content={
                <HexColorPicker
                  style={{ margin: "20px" }}
                  color={dashboardCardFormState.chartColor}
                  onChange={onChangeColor}
                />
              }
            >
              <Button color={dashboardCardFormState.chartColor}>
                <SquareIcon />
              </Button>
            </CustomPopover>
          }
        ></TextField>
      </Grid>
      <Grid
        rowGap="15px"
        columnGap="15px"
        padding="0px 20px 20px 20px"
        templateColumns={{ base: "1fr", large: "1fr 1fr" }}
        alignItems="end"
      >
        <SelectField
          name="period"
          columnSpan={
            dashboardCardFormState.period === periods[periods.length - 1]
              ? 1
              : 2
          }
          descriptiveText={
            dashboardCardFormState.period === periods[periods.length - 1] &&
            !!dashboardCardFormState.dateFrom
              ? `${moment(dashboardCardFormState.dateFrom).format(
                  "YYYY/MM/DD HH:mm"
                )} - ${moment(dashboardCardFormState.dateTo).format(
                  "YYYY/MM/DD HH:mm"
                )}`
              : ""
          }
          value={dashboardCardFormState.period}
          label={
            <>
              <LabelRequired text={strings.fields.period.label} />{" "}
              {dashboardCardFormState.period ===
                periods[periods.length - 1] && (
                <EditCalendarIcon
                  fontSize="small"
                  cursor="pointer"
                  onClick={onOpenModalDate}
                />
              )}
            </>
          }
          onChange={onChange}
          errorMessage={errors["period"]}
          hasError={!!errors["period"]}
        >
          <option value="">{strings.selectOption}</option>
          {periods
            .filter((_per, ind) =>
              initialValues?.graphBy === "company" ||
              initialValues?.graphBy === "branch"
                ? ind !== 0
                : true
            )
            .map((val) => (
              <option value={val}>{globalStrings.dateItems[val]}</option>
            ))}
        </SelectField>
        {dashboardCardFormState.period === periods[periods.length - 1] && (
          <SelectField
            name="groupBy"
            value={dashboardCardFormState.groupBy || ""}
            label={strings.fields.groupBy.label}
            onChange={onChange}
          >
            <option value="">{strings.selectOption}</option>
            {Object.entries(strings.fields.groupBy.options).map((val) => (
              <option value={val[0]}>{val[1]}</option>
            ))}
          </SelectField>
        )}
      </Grid>
      <Flex padding="20px" paddingTop="0px" justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={strings.fields.buttons.save}
          onClick={onSubmitForm}
          variation="primary"
          isDisabled={Object.values(errors).some((e) => !!e)}
        ></Button>
      </Flex>
      <CModal
        alignment="center"
        visible={modalDateVisible}
        onClose={() => setModalDateVisible(false)}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.selectDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            initialValues={{
              from:
                dashboardCardFormState.dateFrom &&
                moment(dashboardCardFormState.dateFrom).toDate(),
              to:
                dashboardCardFormState.dateTo &&
                moment(dashboardCardFormState.dateTo).toDate(),
            }}
            onSubmit={onSubmitDateRange}
            withHours
          />
        </CModalBody>
      </CModal>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      variable: {
        label: "Variable",
      },
      period: {
        label: "Periodo",
      },
      scheduleType: {
        label: "Tipo de Horario",
      },
      groupBy: {
        label: "Agrupar por",
        options: {
          minute: "Minuto",
          hour: "Hora",
          day: "Día",
          week: "Semana",
          month: "Mes",
        },
      },
      chartColor: {
        label: "Color de Gráfica",
      },
      buttons: {
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    selectOption: "Seleccione una opción",
    selectDateRange: "Seleccionar Rango de Fecha",
    selectColor: "Seleccionar Color",
    responses: {
      errorFetchVariables: "Error al consultar variables",
      invalidGroup: "Agrupación inválida para el rango de fecha seleccionado",
    },
  },
});

export default DashboardCardForm;
